import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import  HSBControls  from './Camera/HSBControls';
import { Sides, ViewCube } from './ViewCube';
import { SceneManager } from './Managers/SceneManager';
import { ViewerScene } from './ViewerScene';
import { HsbProject } from '../hsb-project';
import { MaterialManager } from './Managers/MaterialManager';
import { SceneLoader } from './SceneLoader';
import { HsbElement, HsbEntity, HsbModel, SceneLayerName } from '../hsbcad-model';
import { ViewerEvents } from './Events/ViewerEvents';
import { ViewerTool } from './Tools/ViewerBaseTool';
import { SelectionTool } from './Tools/SelectionTool';
import { MeasureTool } from './Tools/Measurement/MeasureTool';
import { SectionTool } from './Tools/SectionTool';
/**
 * @todo dependent on site data. Implement seperation.
 */
//import { IssueTool } from './Tools/IssueTool';
import { SVGOverlay } from './Overlays/SVGOverlay';
import { Overlay } from './Overlays/Overlay';
import { AntiAliasing, PostProcessingManager } from './PostProcessing';
import { Picker } from './Tools/Picker';
import { ViewerLayers } from './Enums/ViewerLayers';
import { CameraUtils } from './Tools/CameraUtils';
import { CameraView, IShareData, ShareData } from '../isharedata';
import { HsbVisual } from '../hsb-visual';
import { SceneKeyManager } from './Managers/SceneKeyManager';
import { LoadingProgress, LoadingSteps } from './LoadingProgress';
import { HsbProjectsCacheService } from '../hsb-projects-cache.service';
import { NodeLoaderService } from '../node-loader.service';
import { IShareSessionBase } from '../IShareSessionBase';
import { IProjectDB } from '../iproject-db';
import './Helpers/Math';
import VisualLogger from './Helpers/Logger';
import Formatting from './Helpers/Formatting';
import { Buffer } from 'buffer';
import { ScreenCapture } from './Tools/ScreenCapture';
import { IssueManager } from './Managers/IssueManager';
import Issue from './Data/Issue';
import { Box3 } from 'three';

export const nodeLoaderService: NodeLoaderService = new NodeLoaderService();
export const hsbProjectsCacheService: HsbProjectsCacheService = new HsbProjectsCacheService();

export type Space = 'NormalizedDeviceSpace' | 'ScreenSpace';

enum AccessMode {
    Guest = 0,
    User = 1,
}

export class Viewer {
    private _accessMode: AccessMode = 0;
    frameRequest: any;
    public get accessMode(): AccessMode {
        return this._accessMode;
    }
    public set accessMode(v: AccessMode) {
        this._accessMode = v;
    }

    private _project: HsbProject | null = null;
    resizeOperation: number = 0;
    public get project(): HsbProject | null {
        return this._project;
    }
    _renderer!: THREE.WebGLRenderer;
    private _renderOperation: number | null = null;
    /**
     * The desired amount of FPS (this will only be the max FPS, could be lower)
     */
    private _FPS: 60 | 30 | 24 = 30;
    public get FPS(): 60 | 30 | 24 {
        return this._FPS;
    }
    public set FPS(v: 60 | 30 | 24) {
        this._FPS = v;
    }
    private _LimitFPS: boolean = true;

    /**
     * Limit the FPS to a max of 60 frames per second.
     * Be aware that browsers have built-in VSync.
     */
    public get LimitFPS() {
        return this._LimitFPS;
    }
    /**
     * VSync throttles the FPS to a max fps of 60.
     * Be aware that browsers have built-in VSync.
     */
    public set LimitFPS(state: boolean) {
        this._LimitFPS = state;
    }
    // Camera tools

    private _cameraUtils: CameraUtils;
    public get cameraUtils() {
        return this._cameraUtils;
    }

    // Overlay tools

    private _SVGOverlay: SVGOverlay;
    public get SVGOverlay() {
        return this._SVGOverlay;
    }

    private _overlay: Overlay;
    public get Overlay() {
        return this._overlay;
    }
    private _active: boolean | undefined;
    public get Active() {
        return this._active;
    }
    _self: Viewer | undefined;

    private _postProcessingManager: PostProcessingManager;
    public get postProcessingManager() {
        return this._postProcessingManager;
    }
    public postProcessingEnabled = false;

    // Scenes
    public get SceneManager() {
        return this._sceneManager;
    }
    private _sceneManager: SceneManager;

    public get SceneKeyManager() {
        return this._sceneKeyManager;
    }
    private _sceneKeyManager: SceneKeyManager;

    private _screenCapture: ScreenCapture;
    public get screenCapture(): ScreenCapture {
        return this._screenCapture;
    }

    public get RenderDataURLSnapshot() {
        return this._renderDataURLSnapshot;
    }
    private _renderDataURLSnapshot: any = false;

    /**
     * Returns the currently active scene, null if no scene is active
     */
    get currentScene(): ViewerScene | null {
        return this._sceneManager.currentScene;
    }
    _scenes: Array<THREE.Scene> = new Array<THREE.Scene>();
    _models: THREE.Object3D | undefined;

    /**
     * Handles all the events from the model viewer.
     */
    public get ViewerEvents() {
        return this._viewerEvents;
    }
    private _viewerEvents: ViewerEvents | null = null;

    // TOOLS
    _selectionTool: SelectionTool | null = null;
    _measureTool: MeasureTool | null = null;
    _currentTool: ViewerTool | null = null;
    _sectionTool: SectionTool | null = null;
    private _issueManager: IssueManager | null = null;
    public get IssueManager(): IssueManager | null {
        return this._issueManager;
    }
    public get CurrentTool() {
        return this._currentTool;
    }
    public set CurrentTool(tool: ViewerTool | null) {
        this._currentTool?.Deactivate();
        this._currentTool = tool;
        this._currentTool?.Activate(this);
    }

    get currentSelection(): HsbEntity[] {
        return this._selectionTool?.currentSelection ?? [];
    }

    private _formatting: Formatting;
    public get formatting(): Formatting {
        return this._formatting;
    }
    public set formatting(v: Formatting) {
        this._formatting = v;
    }

    //Grid
    get grid(): THREE.GridHelper | null | undefined {
        return this._sceneManager.currentScene?._grid;
    }

    // Cameras
    /**
     * Returns the current active camera in the active scene, returns null if no scene active or no camera active
     */
    get currentCamera(): THREE.OrthographicCamera | THREE.PerspectiveCamera | null {
        return this._sceneManager.currentScene?.currentCamera ?? null;
    }
    _picker: Picker;
    _viewCube?: ViewCube;

    // Controls
    /**
     * Returns the current active controls in the active scene, returns null if no scene active or no controls active
     */
    get currentControls(): HSBControls | null {
        return this._sceneManager.currentScene?.CameraControls ?? null;
    }
    _trackballControls: TrackballControls | null = null;
    _orbitControls: OrbitControls | null = null;
    _hsbControls: HSBControls | null = null;

    /**
     * Container for the viewer
     */
    _container: HTMLElement;
    readonly startHeight: number = 0;
    _width: number = 0;
    _height: number = 0;
    _dimensions: THREE.Vector2 = new THREE.Vector2(0, 0);
    _resizeObserver?: ResizeObserver;

    // HSB
    _sceneLoader: SceneLoader;
    private _loadingProgress: LoadingProgress;
    public get loadingProgress(): LoadingProgress {
        return this._loadingProgress;
    }
    _materialManager?: MaterialManager;

    selectedSheetingZones: any;

    private _logger: VisualLogger | null = null;
    public get logger(): VisualLogger | null {
        return this._logger;
    }
    public set logger(v: VisualLogger | null) {
        this._logger = v;
    }

    // Loading progress
    private _progress: ProgressEvent | null = null;
    public get progress(): ProgressEvent | null {
        return this._progress;
    }

    private devicePixelRatio: any = 1;

    /**
     *description
     */
    private _renderSource: string = '';
    /**
     *description
     */
    public get renderSource(): string {
        return this._renderSource;
    }

    constructor(mainCanvas: any, dimensions?: { width: number; height: number }) {
        let self = this;

        if (!dimensions)
            dimensions = {
                width: mainCanvas.getBoundingClientRect().width,
                height: mainCanvas.getBoundingClientRect().height,
            };

        let renderCanvas = document.createElementNS('http://www.w3.org/1999/xhtml', 'canvas');
        renderCanvas.setAttribute('name', 'renderCanvas');
        renderCanvas.style.background = '#e0dfda';
        // Main renderer
        this._renderer = new THREE.WebGLRenderer({
            antialias: true,
            canvas: renderCanvas,
        });

        this._renderer.setClearColor(new THREE.Color(0xe0dfda));

        if (dimensions) {
            this.startHeight = dimensions.height;
            this._width = dimensions.width;
            this._height = dimensions.height;
            this._renderer.setSize(dimensions.width, dimensions.height);
            this._renderer.setPixelRatio(this.devicePixelRatio);
        }

        // Main Canvas
        this._container = mainCanvas;
        mainCanvas.style.background = '#e0dfda';
        this._renderer.domElement.style.background = '#e0dfda';
        this._renderer.domElement.style.visibility = 'hidden';
        this._container.appendChild(this._renderer.domElement);
        this._container.addEventListener('click', () => {
            if (document.activeElement !== this._container) {
                this._container.focus();
            }
        });

        // LoadingPRogress
        this._loadingProgress = new LoadingProgress();

        // Events
        this._viewerEvents = new ViewerEvents(this._renderer.domElement, this);

        // Formatting
        this._formatting = new Formatting();

        // SVG Overlays
        this._SVGOverlay = new SVGOverlay(this, this._container, dimensions);
        this._overlay = new Overlay(this, mainCanvas, dimensions);

        // Scene manager
        this._sceneManager = new SceneManager(this);
        this.createDefaultScene(true);

        this._picker = new Picker(this);

        this._sceneKeyManager = new SceneKeyManager(this);

        // Post process
        this._postProcessingManager = new PostProcessingManager(this, dimensions);

        // Tools
        this._selectionTool = new SelectionTool(this);
        this._measureTool = new MeasureTool(this);
        this._sectionTool = new SectionTool(this);

        this._issueManager = new IssueManager(this);
        // this._issueTool = new IssueTool(this);
        this.CurrentTool = null;

        this._cameraUtils = new CameraUtils(this);
        this._screenCapture = new ScreenCapture(this, this.cameraUtils, nodeLoaderService);

        // Controls
        let viewDiv = document.createElement('ViewCube');
        viewDiv.id = 'Viewcube';
        viewDiv.title = 'Click a corner, an edge or a face to orientate the camera.';
        viewDiv.style.zIndex = this._container.style.zIndex + 1;
        viewDiv.style.position = 'absolute';
        viewDiv.style.top = '0px';
        viewDiv.style.left = '0px';
        viewDiv.style.width = '100px';
        viewDiv.style.height = '100px';
        this._container.appendChild(viewDiv);
        this._viewCube = new ViewCube(this, this.currentCamera!, viewDiv, 100, 100);

        // Materials
        this._materialManager = new MaterialManager(this);
        this._sceneLoader = new SceneLoader(this, this._materialManager, this._loadingProgress);

        // What to do on scene change.
        this.SceneManager._sceneEvent.on('change', (scene: ViewerScene) => {
            if (!scene || !self._viewerEvents) return;

            self.currentScene?.RemoveCameraListeners('change', self._viewerEvents.OnCameraEvent);
            self.currentScene?.RemoveCameraListeners('start', self._viewerEvents.OnCameraEvent);
            self.currentScene?.RemoveCameraListeners('end', self._viewerEvents.OnCameraEvent);
            self.currentScene?.RemoveCameraListeners('held', self._viewerEvents.OnCameraEvent);
            self._selectionTool?.SetScene(scene);
            self._measureTool?.SetScene(scene);
            self._sectionTool?.SetScene(scene);
            self._postProcessingManager.ComposeEffects();
            scene.AddCameraListeners(
                'change',
                self._viewerEvents.OnCameraEvent.bind(self._viewerEvents)
            );
            scene.AddCameraListeners(
                'start',
                self._viewerEvents.OnCameraEvent.bind(self._viewerEvents)
            );
            scene.AddCameraListeners(
                'end',
                self._viewerEvents.OnCameraEvent.bind(self._viewerEvents)
            );
            scene.AddCameraListeners(
                'held',
                self._viewerEvents.OnCameraEvent.bind(self._viewerEvents)
            );
            self.SetNoTool();
        });

        // Visual Logger
        this.logger = new VisualLogger(this._container);

        //Window resize
        this._resizeObserver = new ResizeObserver((event) => {
            self.handleResize();
        });
        this._resizeObserver.observe(this._container);

        setTimeout(() => {
            if (!self._renderer) return;
            self._renderer.clear();
            self._renderer.domElement.style.visibility = 'visible';
        });
    }

    /**
     * Initializes necessary services and provides tokens.
     * This step is required for project loading
     * @param serverURL URL of the backend
     * @param session The session data
     */
    public initialize(serverURL: string, session: IShareSessionBase) {
        // Backend
        if (!session?.token) {
            throw new Error('Could not initialize without a token');
        }
        this.accessMode = session.name === 'guest' ? 0 : 1;
        nodeLoaderService.initialize(serverURL, session);
        hsbProjectsCacheService.hsbProjectsLoader.initialize(serverURL, session.token);
        hsbProjectsCacheService.nodeLoaderService.initialize(serverURL, session);
        hsbProjectsCacheService.hsbProjectsLoader._modelmaploader.initialize(
            hsbProjectsCacheService.nodeLoaderService
        );
        hsbProjectsCacheService.setProjectsCache([]);
    }

    // ---- RENDER LOOP -----
    /**
     * Start the render loop op the viwer
     */
    public start(renderSource: string) {
        this._renderSource = renderSource;
        if (this.Active) return;
        if (this._renderOperation) clearTimeout(this._renderOperation);
        if (!this.currentScene) return false;
        this._active = true;
        this.render();

        return true;
    }

    /**
     * Stops the render loop of the viewer
     */
    public stop(renderSource: string) {
        if (!this.Active) return;
        if (renderSource !== 'viewer' && renderSource !== this.renderSource) return;
        this._renderSource = '';
        this._active = false;
        this.requestRenderFrame();
        if (this._renderOperation) clearTimeout(this._renderOperation);
    }

    /**
     * Performs a cleanup for the viewer
     * @returns void if succes, error otherwise
     */
    public cleanUp() {
        if (process.env.NODE_ENV === 'development') this.logger?.log('Cleaning up viewer');
        try {
            this.stop('viewer');
            this._resizeObserver?.unobserve(this._container);
            delete this._resizeObserver;

            this.logger?.cleanup();

            // Tools
            this.SetNoTool();
            //Section
            this._sectionTool?.cleanUp();
            this._sectionTool = null;
            // Measure
            this._measureTool?.cleanUp();
            this._measureTool = null;
            //Selection
            this._selectionTool?.cleanUp();
            this._selectionTool = null;
            // Material
            this._materialManager?.cleanUp();
            delete this._materialManager;
            //Renderer
            this._renderer.domElement.remove();
            this._renderer?.dispose();

            this.disposeActiveScene();
            this._viewCube?.dispose();
            this._viewCube?._container?.remove();
            delete this._viewCube;

            this._loadingProgress.removeAllListeners();
            this._SVGOverlay.cleanUp();
            this._sceneLoader.clear();
            this.postProcessingManager.dispose();
            this.cameraUtils.dispose();
            this.ViewerEvents?.OnDestroy();
            this._viewerEvents = null;
            this._project = null;

            hsbProjectsCacheService.clearAllProjectsCache();
        } catch (err) {
            if (window.location.hostname === 'localhost') {
                console.error(
                    'Viewer::CleanUp(): Something went wrong while trying to perform the clean up!',
                    err
                );
            }
            return {
                message:
                    'Viewer::CleanUp(): Something went wrong while trying to perform the clean up!',
                error: err,
                status: 500,
            };
        }
    }

    /**
     * The update loop of the viewer. Performs necessary updates.
     */
    public update() {
        let self = this;

        if (self.currentControls?.enabled) self.currentControls?.update();
        self._picker?.update();
    }

    /**
     * The render loop of the viewer
     * @returns
     */
    public render() {
        let self = this;

        // If deactivated stop render loop
        if (!this._active || !this._renderer || !this.currentCamera) return;

        // Update
        this.update();
        this._viewCube?.renderFrame();

        // Render
        // If post processing doesnt render we render regularly
        try {
            if (this.currentScene) {
                if (!(this.postProcessingEnabled && this._postProcessingManager.Render()))
                    this._renderer.render(this.currentScene, this.currentCamera);
                this._SVGOverlay.render();
            }
        } catch (error) {
            console.error(error);
        }

        if (this._LimitFPS) {
            // Limit to desired fps
            window.setTimeout(() => {
                self.render();
            }, 1000 / this._FPS);
        } else {
            window.setTimeout(() => {
                self.render();
            });
        }
    }

    /**
     * Renders 1 frame
     * Be careful with usage of this method. Viewer.requestRenderFrame() is the preferred method to render.
     */
    public renderFrame(source?: string) {
        if (!this._renderer || !this.currentCamera) return;
        // Update
        this.update();
        this._viewCube?.updateCamera();
        // Render
        // If post processing doesn't render we render regularly
        try {
            if (this.currentScene) {
                if (!(this.postProcessingEnabled && this._postProcessingManager.Render()))
                    this._renderer.render(this.currentScene, this.currentCamera);
                this._SVGOverlay.render();
            }
            this._viewCube?.renderFrame();
        } catch (error) {
            console.error(error);
        }
    }

    public requestRenderFrame(source?: string) {
        if (this.frameRequest) return;
        let self = this;
        this.frameRequest = window.setTimeout(() => {
            self.renderFrame(source);
            self.frameRequest = null;
        }, 1000 / this._FPS);
    }

    // ----- SCENES ------

    /**
     * Loads the project into the current scene.
     * @param project
     */
    public setupProject(project: any) {
        let self = this;

        return new Promise((resolve, reject) => {
            if (!this.currentScene) reject('No scene was provided for this project!');
            // Check how much models this project has
            let numModels = Object.keys(project.HsbModels).length;

            // If no models no further loading
            if (numModels === 0) {
                self.loadingProgress.finish();
                resolve({
                    status: 204,
                    message: 'No models in project',
                    data: project,
                });
            }
            self.loadingProgress.status = 'Object creation';
            self._sceneLoader
                .CreateVisualLayer(project, self.currentScene!)
                .then(({ visualLayer, roots }) => {
                    project.visualLayer = visualLayer;
                    self.SetupScene(project, roots);
                    resolve({
                        status: 200,
                        message: 'Succesfully set up project',
                        data: project,
                    });
                })
                .catch((error) => {
                    console.error('Viewer::LoadProject():' + error);
                    reject({
                        status: 400,
                        message: 'Failed to load project',
                        error: error,
                    });
                });
            // }
        });
    }

    /**
     * Sets up a scene for the given project and the given (root)objects
     * @param project THe project for which to set up the scene
     * @param roots The root objects for this scene
     */
    private SetupScene(project: any, roots: THREE.Group[]) {
        if (!this.currentScene) throw new Error('No current scene set! Could not setup scene!');
        this._cameraUtils.FocusCameraOnObject(roots, {
            sides: Sides.Left | Sides.Top | Sides.Front,
            setCameraDimensions: true,
        });
        this.resetViewerControls();

        // Units
        let units = Object.values(project.HsbModels).map((m: any) => m.Units.factor);
        let minMaxUnits = [Math.min(...units), Math.max(...units)];
        this.currentCamera!.near = 0.0001 * minMaxUnits[0];
        this.currentCamera!.far = 1000000 * minMaxUnits[1];
        this._project = project;
        this.loadingProgress.status = 'Scene creation';
        this.loadingProgress.value += LoadingSteps.sceneCreation;
    }

    /**
     * Rebinds the viewercontrols camera and controls to the current ones.
     */
    public resetViewerControls() {
        if (this._viewCube) {
            this._viewCube.RefCamera = this.currentCamera;
            this._viewCube.RefControls = this.currentControls;
        }
    }

    /**
     * Disposes the current active scene if present
     */
    public disposeActiveScene() {
        if (!this.currentScene) return;

        this.ViewerEvents?.stop();
        this.disposeScene(this.currentScene);
        this._project = null;
    }

    /**
     * Disposes the given scene
     * @param scene
     */
    public disposeScene(scene: ViewerScene | string) {
        if (scene instanceof ViewerScene) this._sceneManager.DisposeScene(scene);
        if (typeof scene === 'string') {
            let targetScene = this._sceneManager.GetScene(scene);
            if (targetScene) this._sceneManager.DisposeScene(targetScene);
        }
        if (this._viewCube) {
            this._viewCube.stop();
            this._viewCube._refControls = null;
            this._viewCube._refCamera = null;
        }

        this._SVGOverlay.clear();
    }

    /**
     * Creates a scene with the given name
     * @param name The name for the scene
     * @param activate If true the scene will be set as the active scene.
     */
    public createScene(name: string, activate: boolean = true): ViewerScene {
        let scene = this._sceneManager.CreateEmptyScene(name, activate);
        if (activate) this.ViewerEvents?.resume();
        return scene;
    }

    /**
     * Creates a default scene.
     */
    public createDefaultScene(activate: boolean = false): ViewerScene {
        let scene = this._sceneManager.CreateDefaultScene(activate);
        if (activate) this.ViewerEvents?.resume();
        return scene;
    }

    /**
     * Sets the visibility for the given entity/entities
     * @param entity The entity/entities for which to set the visibility
     * @param visibile True = visible, false = hidden
     * @deprecated Please use {@link {Viewer.HideEntity()}} or {@link {Viewer.ShowEntity()}}
     */
    public setVisibility(
        entity: HsbElement | HsbEntity | (HsbElement | HsbEntity)[],
        visible: boolean
    ) {
        if (entity) {
            visible ? this.ShowEntity(entity) : this.HideEntity(entity);
        } else {
            visible ? this.ShowEverything() : this.HideEverything();
        }

        this.ViewerEvents?.VisibilityEvents.emit('visibility', entity, visible);
    }

    /**
     * Toggles an instance of an instancedmesh based on the given index and state
     * @param obj The instancedmesh for which to change visibility
     * @param instanceId The instanceIndex for which to set the visibility
     * @param visible true if should become visible, false if should become hidden
     */
    toggleInstancedMesh(obj: THREE.InstancedMesh, instanceId: number, visible: boolean) {
        // If already in the correct state early out
        if (visible ? instanceId < obj.count : instanceId >= obj.count) return;
        // If visible we want to swap with first invisible (obj.count),
        // If hidden we want to swap with the last visible (obj.count - 1)
        let swapIndex = obj.count - +!visible;
        // Wireframe
        let instanceHandle = obj.userData.handle[instanceId];
        let wireframes = obj.getObjectByName('wireframes');
        if (visible && wireframes) wireframes.visible = true;

        let wireframe = wireframes?.getObjectByName('wireframe:' + instanceHandle);
        if (wireframe) {
            wireframe.visible = visible;
        }
        // First check if already in correct position
        if (swapIndex !== instanceId) {
            if (!this.project) return;
            // Get data to hide
            let cachedM4 = new THREE.Matrix4();
            obj.getMatrixAt(instanceId, cachedM4);
            let cachedColor = new THREE.Color();
            obj.getColorAt(instanceId, cachedColor);
            // Get last visible
            let lastM4 = new THREE.Matrix4();
            obj.getMatrixAt(swapIndex, lastM4);
            let lastColor = new THREE.Color();
            obj.getColorAt(swapIndex, lastColor);
            // Perform swap
            obj.setMatrixAt(instanceId, lastM4);
            obj.setColorAt(instanceId, lastColor);
            obj.setMatrixAt(swapIndex, cachedM4);
            obj.setColorAt(swapIndex, cachedColor);
            // Update entities
            let swapHandle = obj.userData.handle[swapIndex];
            let instanceEntity = this.project.getEntity(obj.userData.modelId, instanceHandle);
            let swapEntity = this.project.getEntity(obj.userData.modelId, swapHandle);
            // If entities not found
            if (!swapEntity || !instanceEntity) return;
            swapEntity.instanceId = instanceId;
            instanceEntity.instanceId = swapIndex;

            obj.userData.visible[instanceId] = visible;

            // Update props
            Object.entries(obj.userData).forEach(([key, values]) => {
                if (!Array.isArray(values)) return;
                let temp = values[instanceId];
                values[instanceId] = values[swapIndex];
                values[swapIndex] = temp;
            });
        }
        // Increase count to show, Decrease count to hide
        obj.count += +visible * 2 - 1;
        // Update
        obj.instanceMatrix.needsUpdate = true;
        // obj.instanceColor.needsUpdate = true;
    }

    /**
     * Hides the given object
     * @param obj
     */
    HideObject(obj: THREE.Object3D, instanceId?: number) {
        if (obj instanceof THREE.InstancedMesh) {
            if (instanceId !== undefined) this.toggleInstancedMesh(obj, instanceId, false);
            else if (obj.count > 0) {
                // hide all
                obj.count = 0;
                obj.instanceMatrix.needsUpdate = true;
                if (obj.instanceColor) obj.instanceColor.needsUpdate = true;
                obj.userData.visible.fill(false);
                obj.getObjectByName('wireframes')?.traverse((o) => {
                    o.visible = false;
                });
            }
        } else {
            // if (obj.userData.originalmaterial && (obj as any).material)
            //     (obj as any).material = ghostMat;
            // else
            obj.visible = false;
            obj.userData.visible = false;
            let wf = obj.getObjectByName('wireframe:' + obj.userData.handle);
            if (wf) {
                wf.visible = false;
                wf.userData.visible = false;
            }
        }
    }
    /**
     * Shows the given object
     * @param obj
     */
    ShowObject(obj: THREE.Object3D, instanceId?: number) {
        let self = this;
        if (obj instanceof THREE.InstancedMesh) {
            if (instanceId !== undefined) this.toggleInstancedMesh(obj, instanceId, true);
            else if (obj.count !== obj.instanceMatrix.count) {
                // show all
                obj.count = obj.instanceMatrix.count;
                obj.instanceMatrix.needsUpdate = true;
                if (obj.instanceColor) obj.instanceColor.needsUpdate = true;
                obj.matrixWorldNeedsUpdate = true;
                obj.userData.visible.fill(true);
                obj.getObjectByName('wireframes')?.traverse((o) => {
                    o.visible = true;
                });
            }
        } else {
            // if (obj.userData.originalmaterial && (obj as any).material)
            //     (obj as any).material = obj.userData.originalmaterial;
            // else
            obj.visible = true;
            obj.userData.visible = true;
            let wf = obj.getObjectByName('wireframe:' + obj.userData.handle);
            if (wf) {
                wf.visible = true;
                wf.userData.visible = true;
            }
        }
    }

    HideEntity(entity: HsbEntity | HsbEntity[]): void {
        if (Array.isArray(entity)) return entity.forEach((e) => this.HideEntity(e));
        if (this.currentSelection.includes(entity)) this._selectionTool?.Deselect(entity);
        if (!entity.object) return;
        if (entity.object instanceof THREE.InstancedMesh) {
            let index = entity.object.userData.handle.findIndex((h: string) => h === entity.handle);
            if (index < 0) return;
            this.HideObject(entity.object, index);
        } else {
            this.HideObject(entity.object);
        }
        this.ViewerEvents?.VisibilityEvents.emit('visibility', entity, false);
        this.requestRenderFrame();
    }

    ShowEntity(entity: HsbEntity | HsbEntity[]): void {
        let self = this;
        if (Array.isArray(entity)) return entity.forEach((e) => this.ShowEntity(e));
        if (!entity.object) return;
        if (entity.object instanceof THREE.InstancedMesh) {
            entity.object.visible = true;
            let index = entity.object.userData.handle.findIndex((h: string) => h === entity.handle);
            if (index < 0) return;
            this.ShowObject(entity.object, index);
            entity.object.traverseAncestors((o) => {
                self.ShowObject(o);
            });
            entity.object.children.forEach((c) => {
                if (c.name.toLowerCase() !== 'wireframes')
                    c.traverse((o) => {
                        self.ShowObject(o);
                    });
            });
        } else {
            this.ShowObject(entity.object);
            entity.object.traverseAncestors((o) => {
                self.ShowObject(o);
            });
            entity.object.traverse((o) => {
                self.ShowObject(o);
            });
        }
        //Explicit rendering
        this.requestRenderFrame();
    }

    ShowEverything() {
        this.currentScene?.GetLayer(SceneLayerName.Entities).traverse((obj) => {
            this.ShowObject(obj);
        });
        this.ViewerEvents?.VisibilityEvents.emit('visibility', null, true, {
            source: this,
        });
        this.requestRenderFrame();
    }
    HideEverything() {
        this.currentScene?.GetLayer(SceneLayerName.Entities).traverse((obj) => {
            this.HideObject(obj);
        });
        this.ViewerEvents?.VisibilityEvents.emit('visibility', null, true, {
            source: this,
        });
        this.requestRenderFrame();
    }

    /**
     * Show a sheeting or timber zone
     * @param zones The zone(s) to show
     */
    public showZone(...zones: number[]) {
        this.currentScene?.showZone(...zones);
        this.requestRenderFrame();
    }

    /**
     * Hides (a) sheeting or timber zone(s)
     * @param zones  The zone(s) to hide.
     */
    public hideZone(...zones: number[]) {
        this.currentScene?.hideZone(...zones);
        this.requestRenderFrame();
    }

    /**
     * Sets the selection of the viewer for the given entities and state
     * @param entity The entities for which to apply the selection state
     * @param selected True if the given entities are selected, false otherwise
     * @param forceNewSelection If true, will deselect the prior selection.
     * @returns
     */
    public setSelection(entity: any, selected: boolean = true, forceNewSelection: boolean = true) {
        let self = this;
        if (!entity || entity.length === 0) {
            this._selectionTool?.CancelSelection().then((res) => {
                self.ViewerEvents?.ToolEvents?.emit(res.type, {
                    entities: this.currentSelection,
                });
                self.requestRenderFrame();
            });
            return;
        }

        if (forceNewSelection && selected) this._selectionTool?.CancelSelection();
        if (Array.isArray(entity)) {
            this.setSelectionTool();
            let promises: Promise<any>[] = [];
            entity.forEach((element) => {
                if (element instanceof HsbEntity) {
                    let prom = selected
                        ? (this._currentTool as SelectionTool).Select(element, true)
                        : (this._currentTool as SelectionTool).Deselect(element);
                    promises.push(prom);
                }
            });
            Promise.all(promises).then((results) => {
                let args: any = {
                    entities: this.currentSelection,
                    event: null,
                };
                if (this.currentSelection[0].object) {
                    let screenPos = this.getScreenPos(this.currentSelection[0]?.object);
                    args.event = { offsetX: screenPos?.x, offsetY: screenPos?.y };
                }

                self.ViewerEvents?.ToolEvents?.emit(results[0].type, args);
                self.requestRenderFrame();
            });
        } else if (entity instanceof HsbEntity) {
            this.setSelectionTool();

            (selected
                ? (this._currentTool as SelectionTool).Select(entity, true)
                : (this._currentTool as SelectionTool).Deselect(entity)
            ).then((res) => {
                self.ViewerEvents?.ToolEvents?.emit(res.type, { ...res });
                self.requestRenderFrame();
            });
        }
    }

    /**
     * Isolates the given entities
     * @param entities The entities to isolate
     * @param state Whether the given entities should be isolated
     * @param isGroup IF true will use top down view
     */
    isolateEntity(entities: any, state: boolean) {
        if (entities && state) {
            this._selectionTool?.IsolateEntities(entities);
        } else {
            this.ShowEverything();
        }
    }

    /**
     * Sets the hover state for the provided entity.
     * @param entities The entities/ entity for to set the hover state
     * @param state Whether the entities are hovered or not.
     */
    hoverEntity(entities: any[], state: boolean) {
        if (!this._selectionTool) return;
        if (entities) {
            state
                ? entities.forEach((e) => this._selectionTool!.Hover(e))
                : this._selectionTool.UnHover(entities);
        } else {
            this._selectionTool.cancelHover();
        }
    }

    isObjectVisible(object: THREE.Object3D) {
        if (!object.visible) return false;
        if (object instanceof THREE.InstancedMesh) {
            return object.count === object.instanceMatrix.array.length;
        }
        return true;
    }

    getEntities(visibleOnly?: boolean) {
        let self = this;
        if (!this.currentScene || !this.project) return [];
        let objects = this.currentScene.getAllObjectForLayer(SceneLayerName.Entities);
        if (visibleOnly) objects = objects.filter((o) => o.visible);
        let entities = objects.reduce((accu: any[], object) => {
            let modelId = object.userData.modelId;
            if (object instanceof THREE.InstancedMesh) {
                let handles = visibleOnly
                    ? object.userData.handle.slice(0, object.count)
                    : object.userData.handle;
                let out = handles
                    .map((h) => self.project!.EntityTable[modelId][h])
                    .filter((e) => e);
                accu.push(...out);
            } else if (object.userData.handle) {
                let ent = self.project!.EntityTable[modelId][object.userData.handle];
                if (ent) accu.push(ent);
            }
            return accu;
        }, []);
        return entities;
    }

    // ----- CAMERAS -----
    /**
     * Switches to an orthographic camera
     */
    public setCameraOrthoGraphic() {
        this.currentScene?.SetCameraOrthoGraphic();
        if (this._viewCube) {
            this._viewCube.RefCamera = this.currentCamera;
            this._viewCube.start();
        }

        this.postProcessingManager.ComposeEffects();
    }

    /**
     * Switches to a perspective camera
     */
    public setCameraPerspective() {
        this.currentScene?.SetCameraPerspective();
        if (this._viewCube) {
            this._viewCube.RefCamera = this.currentCamera;
            this._viewCube.start();
        }
        this.postProcessingManager.ComposeEffects();
    }

    /**
     * Sets the camera to the given THREE.Camera
     * @param camera The camera to be used
     */
    public setCamera(camera: THREE.OrthographicCamera | THREE.PerspectiveCamera) {
        this.currentScene?.SetCamera(camera);
        if (this._viewCube) {
            this._viewCube.RefCamera = this.currentCamera;
            this._viewCube.start();
        }
        this.postProcessingManager.ComposeEffects();
    }

    /**
     * Returns the current camera view.
     */
    public getCameraView(): CameraView {
        return new CameraView({
            matrix: this.currentCamera?.matrixWorld.clone(),
            zoom: (this.currentCamera as THREE.OrthographicCamera | THREE.PerspectiveCamera).zoom,
            target: this.currentControls?.target ?? new THREE.Vector3(),
        });
    }

    public setCameraView(cameraView: CameraView) {
        if (!this.currentCamera || !this.currentControls) return;
        let view = cameraView.view;
        if (view.matrix?.elements) {
            let matrix = new THREE.Matrix4();
            matrix.elements = view.matrix.elements;
            let pos = new THREE.Vector3(),
                rot = new THREE.Quaternion(),
                scale = new THREE.Vector3();
            matrix.decompose(pos, rot, scale);
            this.currentCamera.position.copy(pos);
            this.currentCamera.quaternion.copy(rot);
        }
        if (view.zoom) {
            this.currentCamera.zoom = view.zoom;
        }
        if (view.target) {
            this.currentControls.target = new THREE.Vector3(
                view.target.x,
                view.target.y,
                view.target.z
            );
        }
        this.currentCamera.updateMatrix();
        this.currentControls.update();
    }

    public setCameraTransform(trans: THREE.Matrix4) {
        if (!(trans instanceof THREE.Matrix4) || !this.currentCamera) return;
        let pos = new THREE.Vector3(),
            rot = new THREE.Quaternion(),
            scale = new THREE.Vector3();
        trans.decompose(pos, rot, scale);
        this.currentCamera.position.copy(pos);
        this.currentCamera.quaternion.copy(rot);

        this.currentCamera.updateMatrix();

        this.currentControls?.update();
    }

    /**
     * Set the HTML container for the DOMelement of the viewer.
     * @param container
     */
    public setContainer(container: HTMLElement) {
        if (this._container) {
            this._container.removeEventListener('resize', this.handleResize.bind(this));
            if (this._renderer) this._container.removeChild(this._renderer.domElement);
            if (this._viewCube?._container) this._container.removeChild(this._viewCube._container);
            this._container.remove();
        }
        this._container = container;
        if (this._renderer) this._container.appendChild(this._renderer.domElement);
        if (this._viewCube?._container) this._container.appendChild(this._viewCube._container);
        this._container.onresize = (event: UIEvent) => {
            this.handleResize();
        };
        // this._container.addEventListener('resize', this.handleResize.bind(this));
        this._SVGOverlay.setContainer(container);
        this._overlay.setContainer(container);
        this.handleResize();
    }
    /**
     * Handles the resizing of this canvas
     * @param event
     */
    public handleResize() {
        if (!this._container) return;
        let oldWidth = this._width,
            oldHeight = this._height;
        let oldDimensions = new THREE.Vector2(this._width, this._height);
        let newDimensions = new THREE.Vector2(
            this._container.offsetWidth,
            this._container.offsetHeight
        );
        this._width = this._container.offsetWidth;
        this._height = this._container.offsetHeight;
        const aspect = this._width / this._height;

        this.resizeOperation++;
        this.currentScene?.handleResize(aspect, newDimensions, oldDimensions);
        this._renderer?.setSize(this._width, this._height);
        let diam = Math.sqrt(Math.pow(this._width, 2) + Math.pow(this._height, 2));
        this._renderer?.setPixelRatio(window.devicePixelRatio * Math.min(2000 / diam, 1));

        // Overlays
        this._SVGOverlay?.handleResize();
        //Postproc
        this._postProcessingManager?.handleResize(this._width, this._height);

        this.renderFrame();
    }

    // ------ TOOLS --------
    /**
     * Sets the current tool to be the selection tool
     */
    public SetNoTool() {
        if (!this._currentTool) return;

        this._currentTool.Deactivate();
        this.CurrentTool = null;
        this.ViewerEvents?.ToolEvents.emit('change', { data: 'none' });
    }

    /**
     * Sets the current tool to be the selection tool
     */
    public setSelectionTool() {
        if (this._currentTool === this._selectionTool) return;

        this.CurrentTool = this._selectionTool;
        this.ViewerEvents?.ToolEvents.emit('change', { data: 'selection' });
    }

    /**
     * Sets the current tool to be the measure tool
     */
    public setMeasureTool() {
        if (this._currentTool === this._measureTool) return;

        this.CurrentTool = this._measureTool;
        this.ViewerEvents?.ToolEvents.emit('change', { data: 'measure' });
    }

    /**
     * Sets the current tool to be the section tool.
     */
    public setSectionTool() {
        if (this._currentTool === this._sectionTool) return;

        this.CurrentTool = this._sectionTool;
        this.ViewerEvents?.ToolEvents.emit('change', { data: 'section' });
    }

    public loadIssues() {
        if (!this.project || !this._issueManager) return;
        // Issues
        let issues = this.project.nodeModel.getNodesMetadataIdByItemType(
            this.project.nodeModel.ItemsRoot,
            'hsbshare.issue'
        );
        this._issueManager.loadIssuesNodes(issues);
    }

    /**
     * Returns the name of the currently active tool
     * @returns THe name of the currently active tool
     */
    public getCurrentToolName(): string {
        switch (this._currentTool) {
            case this._selectionTool:
                return 'selection';
            case this._measureTool:
                return 'measure';
            case this._sectionTool:
                return 'section';
            // case this._issueTool: return 'issue';
            default:
                return 'none';
        }
    }

    /**
     * Returns the name of the currently active camera
     * @returns The name of the current active camera
     */
    public getCurrentCameraName(): string {
        if (this.currentCamera instanceof THREE.OrthographicCamera) {
            return 'orthographic';
        } else if (this.currentCamera instanceof THREE.PerspectiveCamera) {
            return 'perspective';
        }
        return 'custom';
    }

    /**
     * Toggles the visibility of the layers forthe render
     * @param state True = visible, false = hidden.
     * @param layer The layer for which to apply the state.
     */
    public ToggleRenderLayer(state: boolean, layer: ViewerLayers) {
        if (!this.currentScene || !this.currentCamera) return;
        if (state) this.currentCamera.layers.enable(layer);
        else this.currentCamera.layers.disable(layer);
    }

    setAntiAlias(value: 'SSAA' | 'FXAA' | 'MSAA' | 'SMAA' | 'NONE') {
        switch (value) {
            case 'SSAA':
                this.postProcessingManager.AntiAliasing = AntiAliasing.SSAA;
                break;
            case 'FXAA':
                this.postProcessingManager.AntiAliasing = AntiAliasing.FXAA;
                break;
            case 'MSAA':
                this.postProcessingManager.AntiAliasing = AntiAliasing.MSAA;
                break;
            case 'SMAA':
                this.postProcessingManager.AntiAliasing = AntiAliasing.SMAA;
                break;
            case 'NONE':
                this.postProcessingManager.AntiAliasing = AntiAliasing.NONE;
                break;
        }
    }

    setBackground(color: string) {
        let bg = new THREE.Color(color);
        if (bg) {
            this._renderer?.setClearColor(color);
        }
    }

    /**
     * Returns the screenpos for the desired data
     * @param data The data for which to fetch the screenpos
     * @param {'ScreenSpace' | 'NormalizedDeviceSpace'} space Desired space (ScreenSpace or NDC)
     * @returns
     */
    getScreenPos(data: THREE.Vector3 | THREE.Object3D, space: Space = 'ScreenSpace') {
        if (!this.currentCamera) return null;
        let pos: THREE.Vector3 =
            data instanceof THREE.Vector3 ? data : data.getWorldPosition(new THREE.Vector3());
        let projPos = pos.project(this.currentCamera);
        switch (space) {
            case 'NormalizedDeviceSpace':
                return projPos;
            case 'ScreenSpace':
            default:
                return new THREE.Vector3(
                    ((projPos.x + 1) * this._width) / 2,
                    (( 1- projPos.y  ) * this._height) / 2,
                    0
                );
        }
    }

    /**
     * Sets the formatting for the units
     * @param formatting The desired formatting for the units
     */
    setFormatting(formatting: any) {
        this.formatting.unitSettings = formatting.unitSettings;
        this._measureTool?.updateFormatting();
    }

    addSceneKey() {
        this.SceneKeyManager.addSceneKey();
    }

    // Backend
    handleFileDownload(filePath: string) {
        hsbProjectsCacheService.nodeLoaderService.previewFile(filePath);
    }

    /**
     * Logs the provided data in the visual logger
     * @param data The data to log
     * @param style THe preferred style for this log line (use CSS string)
     */
    public log(data: any, style?: string) {
        this.logger?.log(data, style);
    }

    public testIssueCapture(entities: HsbEntity[]): Promise<string> {
        let objects = entities.filter((e) => e.object).map((e) => e.object!);
        this._selectionTool?.CancelSelection();
        // Setting everything else to invisible
        this.HideEverything();
        this.ShowEntity(entities);
        var out = this.screenCapture.requestScreenshot({ target: objects });
        let self = this;
        out.finally(() => {
            self.ShowEverything();
            self.setSelection(entities);
        });
        return out;
    }

    /**
     * Loads and returns the desired project.
     * @param inProject THe project ID or the project data
     * @param options Options for loading. ForceFetch forces a fetch from the backend
     * @returns The loaded project
     */
    async loadProject(
        inProject: string | HsbProject,
        options?: Partial<{ forceFetch: boolean }>
    ): Promise<any> {
        try {
            if (!inProject)
                return await Promise.reject(
                    'Viewer::loadProject(): No project or project ID provided!'
                );
            let self = this;
            this._loadingProgress.reset();
            // Project entry in DB (contains geometry)
            let projectDB: IProjectDB | null = null;
            // The project ID
            let projectID: string = '';
            // Loaded project info
            let project: HsbProject | null = null;

            this.loadingProgress.status = 'Fetching project';
            if (typeof inProject === 'string') {
                projectID = inProject;

                // Notify user histroy
                try {
                    if (this.accessMode === AccessMode.User)
                        await nodeLoaderService.registerProjectOpened(projectID);
                } catch (err) {
                    console.error(`Could not register opening project with id ${projectID}`);
                }
                // Checking if proj has entry in DB
                if (options?.forceFetch) {
                    hsbProjectsCacheService.clearProjectsCache(projectID);
                }
                projectDB = hsbProjectsCacheService.findProjectDBFromID(inProject);
                if (!projectDB) {
                    // no entry yet so have to load geometry
                    let projectDBReq = await nodeLoaderService.getProjectModelNodes(inProject);
                    if (!projectDBReq?.data?.projects[0]) {
                        if (window.location.hostname === 'localhost')
                            console.error(
                                `Did not receive model data from backend for project ID ${inProject}.`
                            );
                        return await Promise.resolve({
                            status: 204,
                            message: 'There is no model data in this project.',
                        });
                    }
                    projectDB = projectDBReq.data.projects[0];
                    await hsbProjectsCacheService.hsbProjectsLoader.projectsDBs.push(
                        projectDB as IProjectDB
                    );
                }
                try {
                    // Load project info from DB
                    project = await hsbProjectsCacheService.loadProject(projectDB);
                } catch (error) {
                    return await Promise.reject({
                        status: 500,
                        message: 'Viewer::LoadProject(): Could not load from cache!',
                        error: error,
                    });
                }
                // If no project something went really wrong
                if (!project) {
                    return await Promise.reject({
                        status: 500,
                        message: 'Viewer::LoadProject(): Could not load project',
                    });
                }
            }
            // Else no need to fetch project data
            else if (inProject instanceof HsbProject) {
                project = inProject;
                projectID = project.projectID;
                // Notify user histroy
                try {
                    if (this.accessMode === AccessMode.User)
                        await nodeLoaderService.registerProjectOpened(projectID);
                } catch (err) {
                    console.error(`Could not register opening project with id ${projectID}`);
                }
            }

            // Make sure all project model thumbnails have been created. If not, instruct the automation service to create them.
            // Loading progress
            self.loadingProgress.value += LoadingSteps.fetch;
            self.loadingProgress.status = 'Loading geometry buffers';

            self.loadingProgress.onComplete(
                async () => {
                    self.loadIssues();

                    await self.cameraUtils.FocusCameraInDirection({
                        sides: Sides.Left | Sides.Top | Sides.Front,
                    });

                    //Render
                    self.requestRenderFrame();
                    // Thumbnail
                    if (self.accessMode === AccessMode.User && project && projectDB)
                        self.screenCapture.updateThumbnail(project, projectDB);
                },
                { once: true }
            );

            // Load geombuffers
            await hsbProjectsCacheService.loadGeometryBuffers(project);
            self.loadingProgress.status = 'Creating scene';
            self.createScene(projectID);
            let loadedProject: any = await self.setupProject(project);
            // If proper loading we queue the rendering on finish

            // If we are in dev log that proj loaded
            if (process.env.NODE_ENV === 'development')
                this.logger?.log('Project was loaded', 'color: green');

            return await Promise.resolve(loadedProject);
        } catch (error) {
            return await Promise.reject({
                status: 500,
                message: 'Viewer::LoadProject(): Something went wrong!',
                error: error,
            });
        }
    }
}
